import { atom, useAtom } from 'jotai'
import qs from 'query-string'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUserState } from '~/atoms/UserState'
import { useAnnounce } from '~/components/shared/LiveAnnouncer'
import { useWebSocketService } from '~/hooks/useWebSocketService'
import { useMessageDraft } from '~/pages/Messaging/hooks/useMessageDraft'
import { usePendingMessages } from '~/pages/Messaging/hooks/usePendingMessages'
import { useGetPbxLineExtensions } from '~/queries/extensionQueries'
import {
  useGetThreadMessages,
  useSendMessage,
} from '~/queries/messagingQueries'
import EmitterService from '~/services/EventEmitter/EmitterService'
import { MessagingEmitterEventType } from '~/services/EventEmitter/types/EmitterTypeDefs'
import { PostMessageParams, WebSocketEventType } from '~/types'

import { Message, PaginatedApiResult } from '@mangovoice/types'

const THREAD_REFETCH_INTERVAL_SECONDS = 30 * 1000
const THREAD_CACHE_TIME_SECONDS = 15 * 1000
const PBX_EXTENSIONS_CACHE_TIME_SECONDS = 60 * 1000 * 5 // 5 minutes
const MESSAGE_FETCH_LIMIT = 25
const ANNOUNCED_MESSAGE_MAX_LENGTH = 140

const truncateForAnnouncement = (text: string) =>
  text.length > ANNOUNCED_MESSAGE_MAX_LENGTH
    ? `${text.slice(0, ANNOUNCED_MESSAGE_MAX_LENGTH).trimEnd()}…`
    : text

/**
 * Inbound messages newer than `latestSeenMessageId`. Returns nothing for the
 * first load (`latestSeenMessageId` is null) so existing history isn't read out.
 */
export const getNewInboundMessages = (
  messages: Message[],
  latestSeenMessageId: number | null,
) => {
  if (latestSeenMessageId === null) return []
  return messages.filter(
    (message) =>
      message.direction === 'inbound' && message.id > latestSeenMessageId,
  )
}

interface ThreadMessagesState {
  threadMessages: Message[]
}

const threadMessagesStateAtom = atom<ThreadMessagesState>({
  threadMessages: [],
})

export const useThreadMessagesViewModel = (
  threadId: number,
  contactName?: string,
) => {
  const { t } = useTranslation()
  const announce = useAnnounce()
  const latestSeenMessageId = useRef<number | null>(null)
  const [threadMessagesState, setThreadMessagesState] = useAtom(
    threadMessagesStateAtom,
  )
  const {
    pendingMessages,
    getPendingMessage,
    setPendingMessage,
    removePendingMessages,
  } = usePendingMessages({ threadId })
  const [messageText, setMessageText] = useMessageDraft(threadId)
  const [nextPageUri, setNextPageUri] = useState<string | null>()
  const { pbxId } = useUserState()
  const threadMessagesUpdatedCallback =
    useRef<(data: PaginatedApiResult<Message>) => void>(undefined)

  const announceNewInboundMessages = (results: Message[]) => {
    const newInbound = getNewInboundMessages(
      results,
      latestSeenMessageId.current,
    )
    const latestId = results.reduce(
      (max, message) => Math.max(max, message.id),
      0,
    )
    latestSeenMessageId.current = Math.max(
      latestSeenMessageId.current ?? 0,
      latestId,
    )

    if (newInbound.length === 0) return

    const sender = contactName || t('a11y.messages.senderContact')
    if (newInbound.length === 1) {
      const body = newInbound[0].body?.trim()
      announce(
        t('a11y.messages.newMessage', {
          sender,
          body: body
            ? truncateForAnnouncement(body)
            : t('a11y.messages.attachment'),
        }),
      )
      return
    }
    announce(
      t('a11y.messages.newMessages', { count: newInbound.length, sender }),
    )
  }

  const { refetch: getThreadMessages, isFetching: isThreadFetching } =
    useGetThreadMessages({
      threadId,
      onQuerySuccess: (data) => {
        const results = data.results ?? []
        announceNewInboundMessages(results)
        threadMessagesUpdatedCallback.current?.(data)

        const merged = [...results, ...threadMessagesState.threadMessages]

        setNextPageUri(data.next)
        setThreadMessagesState({
          threadMessages: merged,
        })
        // Remove pending messages that have been sent in case any are still present
        removePendingMessages(results)
      },
      options: {
        refetchOnWindowFocus: true,
        refetchInterval: THREAD_REFETCH_INTERVAL_SECONDS,
        gcTime: THREAD_CACHE_TIME_SECONDS,
      },
    })

  const { data: pbxExtensionsData } = useGetPbxLineExtensions({
    pbxId: pbxId ?? 0,
    options: {
      enabled: true,
      gcTime: PBX_EXTENSIONS_CACHE_TIME_SECONDS,
      staleTime: PBX_EXTENSIONS_CACHE_TIME_SECONDS,
    },
  })

  const { mutate: sendThreadMessageInternal } = useSendMessage({
    onSuccess: (response) => {
      const pendingMessage = getPendingMessage(
        response.postMessageParams.tempUuid,
      )

      if (pendingMessage) {
        setPendingMessage({
          ...pendingMessage,
          messageId: response.response.id,
        })
      }

      announce(t('a11y.messages.messageSent'))

      //fetch for new thread messages with offset
      getThreadMessages({
        offset: 0,
        limit: MESSAGE_FETCH_LIMIT,
      })

      //emit an event
      EmitterService.emit(MessagingEmitterEventType.THREAD_MESSAGE_SENT, {
        threadId,
      })
    },
    onError: (_, postMessageParams) => {
      announce(t('a11y.messages.messageFailed'), 'assertive')

      const pendingMessage = getPendingMessage(postMessageParams.tempUuid)

      if (pendingMessage) {
        setPendingMessage({
          ...pendingMessage,
          status: 'failed',
        })
      }
    },
  })

  const sendThreadMessage = (
    postMessageParams: Omit<PostMessageParams, 'threadId'>,
  ) => {
    const postMessageParamsWithThreadId: PostMessageParams = {
      threadId,
      ...postMessageParams,
    }
    setPendingMessage({
      body: postMessageParams.body ?? postMessageParams.file?.name ?? 'Message',
      status: 'sending',
      uuid: postMessageParams.tempUuid,
      messageType: postMessageParams.file ? 'file' : 'text',
      created_at: new Date(),
      direction: 'outbound',
      originalSendParams: { ...postMessageParamsWithThreadId },
    })
    setMessageText('')
    sendThreadMessageInternal({ ...postMessageParamsWithThreadId })
  }

  const onThreadMessagesUpdated = (
    callback: (data: PaginatedApiResult<Message>) => void,
  ) => {
    threadMessagesUpdatedCallback.current = callback
  }

  // Subscribe to message received events for this thread
  const { subscribe: wssSubscribe } = useWebSocketService()
  wssSubscribe(WebSocketEventType.MessageReceived, {
    subscriptionToken: 'ThreadMessagesViewModel:MessageReceived',
    filter: (event) => event.payload.threadId === threadId,
    callback: (_) => {
      getThreadMessages({
        offset: 0,
        limit: MESSAGE_FETCH_LIMIT,
      })
    },
  })

  const tryGetPaginationParamsOrDefault = (queryString: string) => {
    const params = qs.parse(queryString)
    return {
      threadId: Number(params.thread_id),
      offset: Number(params.offset),
      search: String(params.search),
    }
  }

  const tryGetNextPage = () => {
    if (!nextPageUri) {
      return
    }
    const paginationParams = tryGetPaginationParamsOrDefault(nextPageUri)
    getThreadMessages(paginationParams)
  }

  useEffect(() => {
    getThreadMessages({
      offset: 0,
      limit: MESSAGE_FETCH_LIMIT,
    })
  }, [])

  return {
    threadMessagesState,
    pendingMessages,
    getThreadMessages,
    pbxExtensions: pbxExtensionsData?.results ?? [],
    isThreadFetching,
    onThreadMessagesUpdated,
    nextPageUri,
    tryGetNextPage,
    sendThreadMessage,
    messageText,
    setMessageText,
  }
}
